import { FC, Fragment, useMemo, useState, useEffect } from "react";
import { Box, Grid, Stack, Typography, Skeleton } from "@mui/material";
import { Badge, KeyValueTable, TableToggleField } from "@vilocnv/allsetra-core";
import AssignedUsersTable from "./children/AssignedUsersTable";
import ObjectDetailsCard from "components/cards/ObjectDetailsCard/ObjectDetailsCard";
import {
  ObjectDetailsLeftContainer,
  SmallText
} from "components/cards/ObjectDetailsCard/ObjectDetailsCard.styled";
import ObjectDetailsMap from "./children/ObjectDetailsMap";
import DevicesTable from "./children/DevicesTable";
import DataValuesAndNumber from "./children/DataValuesAndNumber";
import ObjectCorrectionsTabs from "components/sections/objects/ObjectCorrectionsTabs/ObjectCorrectionsTabs";
import WarningIcon from "@mui/icons-material/Warning";

// DATA
import { useTranslation } from "react-i18next";
import { useAppSelector, useAppDispatch } from "hooks";
import {
  transformObjectForObjectInfoTable,
  transformObjectForReminderTable,
  transformObjectForWorkingHoursTable,
  transformObjectForAlarmConfigTable,
  transformObjectForCorrectionTable,
  transformObjectOwnersDataForTable,
  getSplittedAddress,
  getAddressDisplayInfo,
  getResolvedAddressFromStore
} from "app/data/helpers";
import {
  selectAccountFeaturesState,
  selectDrawerSelectedAccountId,
  selectReportsState
} from "app/data/selectors";
import { updateObjectAwayModeThunk } from "app/features/objects/objectsActions";
import { setAwayModeStatus } from "app/features/objects/objectsSlice";
import { resolveAddressThunk } from "app/features";
import {
  LocationTitle,
  RecordKey,
  RecordValue,
  RecordsWrapper,
  ObjectContainer,
  RadiusSelectionBox,
  RecordValueBox
} from "./ObjectDetailsSection.styled";
import { HiddenBadgeIcon } from "components/ui/icons";

interface Props {
  specificObject: any | null;
  alarmsConfig: any;
}

const ObjectDetailsSection: FC<Props> = ({ specificObject, alarmsConfig }) => {
  const dispatch = useAppDispatch();
  const drawerSelectedAccountId = useAppSelector(selectDrawerSelectedAccountId);
  const [isUpdating, setIsUpdating] = useState(false);
  const [resolvedAddressLoading, setResolvedAddressLoading] = useState(false);
  const awayModeStatus = useAppSelector(
    (state) =>
      state.rootReducer.objectsReducer.awayModeStatus[
        specificObject?.uniqueId
      ] ?? false
  );

  const { resolvedAddresses } = useAppSelector(selectReportsState);
  const { features } = useAppSelector(selectAccountFeaturesState);

  const { t } = useTranslation([
    "translation",
    "tableHeadingsTranslation",
    "formFieldsTranslation"
  ]);

  useEffect(() => {
    const latitude = specificObject?.location?.latitude;
    const longitude = specificObject?.location?.longitude;
    const resolvedAddress = specificObject?.location?.resolvedAddress;

    if (
      latitude &&
      longitude &&
      latitude > 0 &&
      longitude > 0 &&
      !resolvedAddress
    ) {
      const existingAddress = resolvedAddresses.find(
        (addr) => addr.latitude === latitude && addr.longitude === longitude
      );

      if (!existingAddress) {
        setResolvedAddressLoading(true);
        dispatch(
          resolveAddressThunk({
            latitude,
            longitude
          })
        ).finally(() => {
          setResolvedAddressLoading(false);
        });
      }
    }
  }, [
    specificObject?.location?.latitude,
    specificObject?.location?.longitude,
    specificObject?.location?.resolvedAddress,
    resolvedAddresses,
    dispatch
  ]);

  const handleAwayModeChange = async (checked: boolean) => {
    if (specificObject?.uniqueId && drawerSelectedAccountId) {
      setIsUpdating(true);
      dispatch(
        setAwayModeStatus({
          objectId: specificObject.uniqueId,
          isEnabled: checked
        })
      );

      try {
        await dispatch(
          updateObjectAwayModeThunk({
            accountId: drawerSelectedAccountId,
            objectId: specificObject.uniqueId,
            isEnabled: checked
          })
        ).unwrap();
      } catch (error) {
        dispatch(
          setAwayModeStatus({
            objectId: specificObject.uniqueId,
            isEnabled: !checked
          })
        );
      } finally {
        setIsUpdating(false);
      }
    }
  };

  const { objectInformation, workingHours, alarmConfiguration, owners } =
    useMemo(() => {
      return {
        objectInformation: transformObjectForObjectInfoTable(specificObject),
        workingHours: transformObjectForWorkingHoursTable(specificObject),
        correction: transformObjectForCorrectionTable(specificObject, t),
        reminder: transformObjectForReminderTable(specificObject, t),
        alarmConfiguration: transformObjectForAlarmConfigTable(alarmsConfig),
        owners: transformObjectOwnersDataForTable(specificObject)
      };
    }, [specificObject, t]);

  const latitude = specificObject?.location?.latitude;
  const longitude = specificObject?.location?.longitude;
  const objectResolvedAddress = specificObject?.location?.resolvedAddress;

  const finalResolvedAddress = getResolvedAddressFromStore(
    objectResolvedAddress,
    resolvedAddresses,
    latitude,
    longitude
  );

  const addressInfo = getAddressDisplayInfo(
    latitude,
    longitude,
    finalResolvedAddress
  );

  return (
    <ObjectContainer>
      <Grid container spacing={4}>
        <Grid sx={{ pl: "48px !important" }} item xs={12} lg={5}>
          <ObjectDetailsLeftContainer spacing={4} mt={4}>
            <Stack spacing={2}>
              <RadiusSelectionBox>
                <Stack spacing={1} sx={{ width: "100%" }}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    sx={{ width: "100%" }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body1" alignItems="center">
                        {t("titles.awayMode")}
                      </Typography>
                      {awayModeStatus && (
                        <WarningIcon
                          color="warning"
                          sx={{ width: "16px", height: "16px" }}
                        />
                      )}
                    </Box>
                    <TableToggleField
                      label={""}
                      name="isAwayMode"
                      onChange={(e) =>
                        !isUpdating && handleAwayModeChange(e.target.checked)
                      }
                      checked={awayModeStatus}
                    />
                  </Box>
                  {awayModeStatus && (
                    <SmallText
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        maxWidth: "300px"
                      }}
                    >
                      {t("descriptions.awayModeDescription")}
                    </SmallText>
                  )}
                </Stack>
              </RadiusSelectionBox>
            </Stack>

            {specificObject && <ObjectDetailsCard object={specificObject} />}

            <ObjectCorrectionsTabs
              specificObject={specificObject}
              accountId={drawerSelectedAccountId ?? ""}
            />
            <LocationTitle>{t("titles.location")}</LocationTitle>
            <Box>
              {resolvedAddressLoading ? (
                <RecordValue>
                  <Skeleton variant="text" width={200} height={20} />
                  <Skeleton variant="text" width={200} height={20} />
                </RecordValue>
              ) : addressInfo.isHidden ? (
                <RecordValueBox>
                  <HiddenBadgeIcon />
                  {t("typography.addressIsHiddenDescription")}
                </RecordValueBox>
              ) : addressInfo.isUnknown ? (
                <RecordValue>
                  <Badge colorScheme="blank" textWeight="light">
                    {t("typography.addressUnknown")}
                  </Badge>
                </RecordValue>
              ) : addressInfo.isNA ? (
                <RecordValue>
                  <Badge colorScheme="blank" textWeight="light">
                    N/A
                  </Badge>
                </RecordValue>
              ) : (
                <Fragment>
                  <RecordValue>
                    {getSplittedAddress(0, finalResolvedAddress || "")}
                  </RecordValue>
                  <RecordValue>
                    {getSplittedAddress(1, finalResolvedAddress || "") +
                      getSplittedAddress(2, finalResolvedAddress || "")}
                  </RecordValue>
                </Fragment>
              )}
            </Box>
            {/* </RecordsWrapper> */}

            {/* <KeyValueTable
              title={t("titles.location")}
              records={{
                [t("address.label", {
                  ns: "formFieldsTranslation"
                })]: `${getAddress(specificObject?.location?.resolvedAddress)}`
              }}
            /> */}

            <AssignedUsersTable specificObject={specificObject} />

            <KeyValueTable
              title={t("titles.objectInfo")}
              records={objectInformation}
            />

            <DataValuesAndNumber
              data={specificObject?.accounts ?? []}
              dataType="accounts"
            />

            <KeyValueTable records={owners} />

            <DataValuesAndNumber
              data={specificObject?.groups ?? []}
              dataType={t("drawerMenuLinks.groups")}
            />
            {specificObject?.isWorkingHoursOverriden ? (
              <KeyValueTable
                title={t("titles.workingHours")}
                records={workingHours}
                customizedWorkingHours={true}
              />
            ) : (
              <KeyValueTable
                title={t("titles.workingHours")}
                records={workingHours}
              />
            )}

            <DevicesTable specificObject={specificObject} />

            <KeyValueTable
              title={t("titles.alarmConfig")}
              records={alarmConfiguration}
              customizedWorkingHours={
                alarmsConfig?.objectHasCustomAlarmConfiguration
              }
            />
          </ObjectDetailsLeftContainer>
        </Grid>

        <Grid item xs={12} lg={7} gap={2}>
          {addressInfo.isHidden ? (
            <Box
              sx={{
                position: "relative",
                borderRadius: "8px",
                overflow: "hidden"
              }}
            >
              <ObjectDetailsMap
                object={specificObject}
                isAddressHidden={true}
              />

              <Box
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  zIndex: 10,
                  padding: 3,
                  pointerEvents: "none"
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "#1D1F2BD9",
                    color: "#FFF",
                    fontSize: 82,
                    gap: 1,
                    "& svg g path": {
                      fill: "#FFF !important"
                    }
                  }}
                >
                  <HiddenBadgeIcon />
                  <Typography variant="body1" color="#FFF" textAlign="center">
                    {t("typography.addressIsHiddenForPrivateRides")}
                  </Typography>
                </Box>
              </Box>
            </Box>
          ) : (
            <ObjectDetailsMap object={specificObject} isAddressHidden={false} />
          )}
        </Grid>
      </Grid>
    </ObjectContainer>
  );
};

export default ObjectDetailsSection;
